import { useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams, useSearchParams } from 'react-router';
import { PageSpinner } from '@/components/LoadingBlock';
import { AccountPage } from '@/features/account/AccountPage';
import { AcceptInvitePage } from '@/features/auth/AcceptInvitePage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { SsoCompletePage } from '@/features/auth/SsoCompletePage';
import { StatusPage } from '@/features/status/StatusPage';
import { SupportRequestsPage } from '@/features/org/SupportRequestsPage';
import { MyRequestsPage } from '@/features/support/MyRequestsPage';
import { SupportRequestPage } from '@/features/support/SupportRequestPage';
import { JobTemplatesPage, OrgTemplatesPage } from '@/features/templates/TemplatesPage';
import { HelpPage } from '@/features/support/HelpPage';
import { PrivacyPage, TermsPage } from '@/features/legal/LegalPage';
import { AutomationPage } from '@/features/automation/AutomationPage';
import { ComposePage } from '@/features/compose/ComposePage';
import { SmsConsentPage, type ConsentMode } from '@/features/consent/SmsConsentPage';
import { ContactsPage } from '@/features/contacts/ContactsPage';
import { HistoryPage } from '@/features/history/HistoryPage';
import { MessageDetailPage } from '@/features/history/MessageDetailPage';
import { RecipientPage } from '@/features/history/RecipientPage';
import { JobOverviewPage } from '@/features/jobs/JobOverviewPage';
import { JobSettingsPage } from '@/features/jobs/JobSettingsPage';
import { JobsPage } from '@/features/jobs/JobsPage';
import { KeywordSignPage } from '@/features/keywords/KeywordSignPage';
import { KeywordsPage } from '@/features/keywords/KeywordsPage';
import { BillingPage } from '@/features/org/BillingPage';
import { InvoicePage } from '@/features/org/InvoicePage';
import { OrgSettingsPage } from '@/features/org/OrgSettingsPage';
import { UsagePage } from '@/features/org/UsagePage';
import { UsageReportsPage } from '@/features/org/UsageReportsPage';
import { JobReportsPage } from '@/features/reports/JobReportsPage';
import { UsersPage } from '@/features/org/UsersPage';
import { TeamPage } from '@/features/team/TeamPage';
import { AppShell } from '@/layouts/AppShell';
import { JobLayout } from '@/layouts/JobLayout';
import { OrgLayout } from '@/layouts/OrgLayout';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { setTenantSlug, tenantSlugFromHost } from '@/lib/tenant';
import { NotFoundPage } from '@/pages/NotFoundPage';

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <PageSpinner />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function RequireOrgAdmin({ children }: { children: ReactNode }) {
  const { isOrgAdmin } = useAuth();
  if (!isOrgAdmin) return <NotFoundPage />;
  return <>{children}</>;
}

/** Pages the platform hid for this organization (console → tenant → Features) behave like they do not exist. */
function RequireFeature({ flag, children }: { flag: 'helpRequests' | 'billing' | 'templates' | 'reports'; children: ReactNode }) {
  const { me } = useAuth();
  if (me && me.tenant.features[flag] === false) return <NotFoundPage />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <PageSpinner />;
  if (status === 'authenticated') return <Navigate to="/jobs" replace />;
  return <>{children}</>;
}

/** Legacy `/login?tenant=siemens` → canonical `/login/siemens` (other query params are kept). */
function LoginEntry() {
  const [params] = useSearchParams();
  const tenant = params.get('tenant');
  if (tenant && /^[a-z0-9-]{1,63}$/i.test(tenant)) {
    const rest = new URLSearchParams(params);
    rest.delete('tenant');
    const qs = rest.toString();
    return <Navigate to={`/login/${tenant.toLowerCase()}${qs ? `?${qs}` : ''}`} replace />;
  }
  // A sign-in domain (siemens-energy.solvent.plus → tenant "siemens") or a tenant host names the organization.
  const hostSlug = tenantSlugFromHost();
  if (hostSlug) {
    const qs = params.toString();
    return <Navigate to={`/login/${hostSlug}${qs ? `?${qs}` : ''}`} replace />;
  }
  return (
    <PublicOnly>
      <LoginPage />
    </PublicOnly>
  );
}

/**
 * `/login/:tenantSlug`: sign in to a specific organization. The slug is remembered for this browser and
 * wins over any other tenant's session. If the app already loaded another tenant's branding (switching
 * organizations in the same tab), a full reload picks up the new one.
 */
function TenantLogin() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { branding, loaded } = useBranding();
  const slug = tenantSlug.toLowerCase();
  if (!/^[a-z0-9-]{1,63}$/.test(slug)) return <Navigate to="/login" replace />;
  setTenantSlug(slug);
  // Reload at most once per tab when another tenant's branding is loaded (e.g. switching organizations in
  // the same tab); if it still does not match after that, show the page anyway rather than loop.
  const reloadKey = `crewline-tenant-reloaded:${slug}`;
  const reloaded = (() => {
    try {
      return sessionStorage.getItem(reloadKey) === '1';
    } catch {
      return true;
    }
  })();
  if (loaded && branding.tenantSlug && branding.tenantSlug !== slug && !reloaded) {
    try {
      sessionStorage.setItem(reloadKey, '1');
    } catch {
      /* ignore */
    }
    window.location.replace(`/login/${slug}`);
    return <PageSpinner />;
  }
  if (loaded && branding.tenantSlug === slug) {
    try {
      sessionStorage.removeItem(reloadKey);
    } catch {
      /* ignore */
    }
  }
  return (
    <PublicOnly>
      <LoginPage />
    </PublicOnly>
  );
}

/** `/opt-in` and `/opt-out`: like /login, a `?tenant=` or a tenant host redirects to the slug form; otherwise the stored tenant is used. */
function ConsentEntry({ mode }: { mode: ConsentMode }) {
  const [params] = useSearchParams();
  const { branding, loaded } = useBranding();
  const tenant = params.get('tenant');
  const hostSlug = tenantSlugFromHost();
  const asked = (tenant && /^[a-z0-9-]{1,63}$/i.test(tenant) ? tenant.toLowerCase() : null) ?? hostSlug;
  // A static page set in the console (the campaign's call to action) replaces the built-in one.
  const override = !asked || branding.tenantSlug === asked ? (mode === 'opt-in' ? branding.legal.signupUrl : branding.legal.optOutUrl) : null;
  useEffect(() => {
    if (loaded && override) window.location.replace(override);
  }, [loaded, override]);
  if (loaded && override) return <PageSpinner />;
  if (asked) return <Navigate to={`/${mode}/${asked}`} replace />;
  return <SmsConsentPage mode={mode} />;
}

/** `/opt-in/:tenantSlug`: remember the organization for this browser and reload once if another tenant's branding is loaded. */
function TenantConsent({ mode }: { mode: ConsentMode }) {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { branding, loaded } = useBranding();
  const slug = tenantSlug.toLowerCase();
  const override = mode === 'opt-in' ? branding.legal.signupUrl : branding.legal.optOutUrl;
  useEffect(() => {
    if (loaded && branding.tenantSlug === slug && override) window.location.replace(override);
  }, [loaded, branding.tenantSlug, slug, override]);
  if (!/^[a-z0-9-]{1,63}$/.test(slug)) return <Navigate to={`/${mode}`} replace />;
  setTenantSlug(slug);
  if (loaded && branding.tenantSlug === slug && override) return <PageSpinner />;
  const reloadKey = `crewline-tenant-reloaded:${slug}`;
  let reloaded = true;
  try {
    reloaded = sessionStorage.getItem(reloadKey) === '1';
  } catch {
    /* ignore */
  }
  if (loaded && branding.tenantSlug && branding.tenantSlug !== slug && !reloaded) {
    try {
      sessionStorage.setItem(reloadKey, '1');
    } catch {
      /* ignore */
    }
    window.location.replace(`/${mode}/${slug}`);
    return <PageSpinner />;
  }
  return <SmsConsentPage mode={mode} />;
}

function JobIndexRedirect() {
  const { jobId } = useParams();
  return <Navigate to={`/jobs/${jobId}/overview`} replace />;
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginEntry /> },
  { path: '/login/:tenantSlug', element: <TenantLogin /> },
  { path: '/opt-in', element: <ConsentEntry mode="opt-in" /> },
  { path: '/opt-in/:tenantSlug', element: <TenantConsent mode="opt-in" /> },
  { path: '/opt-out', element: <ConsentEntry mode="opt-out" /> },
  { path: '/opt-out/:tenantSlug', element: <TenantConsent mode="opt-out" /> },
  /** Landing after a single sign-on round-trip: the refresh cookie is set, the boot refresh picks it up. */
  { path: '/sso/complete', element: <SsoCompletePage /> },
  { path: '/status', element: <StatusPage /> },
  {
    path: '/forgot-password',
    element: (
      <PublicOnly>
        <ForgotPasswordPage />
      </PublicOnly>
    ),
  },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  { path: '/help', element: <HelpPage /> },
  { path: '/legal/privacy', element: <PrivacyPage /> },
  { path: '/legal/terms', element: <TermsPage /> },
  {
    // Printable gate sign: no app chrome so it prints cleanly.
    path: '/jobs/:jobId/keywords/:keywordId/sign',
    element: (
      <RequireAuth>
        <KeywordSignPage />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/jobs" replace /> },
      { path: 'jobs', element: <JobsPage /> },
      { path: 'support', element: <RequireFeature flag="helpRequests"><MyRequestsPage /></RequireFeature> },
      { path: 'support/:requestId', element: <RequireFeature flag="helpRequests"><SupportRequestPage mode="requester" /></RequireFeature> },
      {
        path: 'jobs/:jobId',
        element: <JobLayout />,
        children: [
          { index: true, element: <JobIndexRedirect /> },
          { path: 'overview', element: <JobOverviewPage /> },
          { path: 'contacts', element: <ContactsPage /> },
          { path: 'contacts/:contactId', element: <ContactsPage /> },
          { path: 'compose', element: <ComposePage /> },
          { path: 'history', element: <HistoryPage /> },
          { path: 'history/:messageId', element: <MessageDetailPage /> },
          { path: 'history/:messageId/recipients/:recipientId', element: <RecipientPage /> },
          { path: 'keywords', element: <KeywordsPage /> },
          { path: 'keywords/:keywordId', element: <KeywordsPage /> },
          { path: 'automation', element: <AutomationPage /> },
          { path: 'automation/:ruleId', element: <AutomationPage /> },
          { path: 'team', element: <TeamPage /> },
          { path: 'templates', element: <RequireFeature flag="templates"><JobTemplatesPage /></RequireFeature> },
          { path: 'reports', element: <RequireFeature flag="reports"><JobReportsPage /></RequireFeature> },
          { path: 'settings', element: <JobSettingsPage /> },
        ],
      },
      {
        path: 'org',
        element: (
          <RequireOrgAdmin>
            <OrgLayout />
          </RequireOrgAdmin>
        ),
        children: [
          { index: true, element: <Navigate to="/org/users" replace /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'users/:userId', element: <UsersPage /> },
          { path: 'reports', element: <RequireFeature flag="reports"><UsagePage /></RequireFeature> },
          { path: 'reports/scheduled', element: <RequireFeature flag="reports"><UsageReportsPage /></RequireFeature> },
          { path: 'usage', element: <Navigate to="/org/reports" replace /> },
          { path: 'usage/reports', element: <Navigate to="/org/reports/scheduled" replace /> },
          { path: 'support', element: <RequireFeature flag="helpRequests"><SupportRequestsPage /></RequireFeature> },
          { path: 'support/:requestId', element: <RequireFeature flag="helpRequests"><SupportRequestPage mode="admin" /></RequireFeature> },
          { path: 'templates', element: <RequireFeature flag="templates"><OrgTemplatesPage /></RequireFeature> },
          { path: 'billing', element: <RequireFeature flag="billing"><BillingPage /></RequireFeature> },
          { path: 'billing/invoices/:invoiceId', element: <RequireFeature flag="billing"><InvoicePage /></RequireFeature> },
          { path: 'settings', element: <OrgSettingsPage /> },
        ],
      },
      { path: 'account', element: <AccountPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  const { loaded } = useBranding();
  // Hold first paint until tenant branding is applied to avoid a color flash.
  if (!loaded) return null;
  return <RouterProvider router={router} />;
}
